import classNames from "classnames";
import { useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { useMutation } from "@apollo/client";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import type { Action } from "@/components/MenuButton";
import { RowHeader, RowLink, TableCardRow, TABLE_CARD_BODY_PADDING } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import useAmplitude from "@/hooks/useAmplitude";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { Path } from "@/router";
import { Link, useNavigate } from "@/router";
import type { CRMTask } from "@/types";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import type { Node } from "@/pages/sections/[sectionId]/change-requests/queries";
import DeleteTaskDialog from "@/pages/tasks/_components/DeleteTaskDialog";
import { UPDATE_TASK_MUTATION, type UpdateTaskResponse } from "@/pages/tasks/_hooks/mutations";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import { taskPreviewSubtitle } from "../_utils/clipTaskDescription";
import { formatDaysUntilDue } from "../_utils/formatDaysUntilDue";
import { MentionedText, displayMentionText } from "@/components/AtMention";
import DashboardPrivateTitle from "./DashboardPrivateTitle";
import DashboardRowDetails from "./DashboardRowDetails";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import DashboardTasksPanelHeader from "./DashboardTasksPanelHeader";
import PendingChangeRequestsTable from "./PendingChangeRequestsTable";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import {
  DASHBOARD_PANEL_BODY_CLASS,
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL
} from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";

const COLUMN_COUNT = 2;
const TASK_ICON = "assignment_turned_in" as const;
const DASHBOARD_TASK_SOURCE = "dashboard" as const;

function DueLabel({ dueDate }: { dueDate: string | null }) {
  const dueLabel = formatDaysUntilDue(dueDate);
  if (!dueLabel) {
    return renderEmptyCellState(null);
  }

  return (
    <span
      className={classNames({
        "text-sys-brand-error": dueLabel.tone === "error",
        "text-sys-brand-on-surface-variant": dueLabel.tone === "default"
      })}
    >
      {dueLabel.label}
    </span>
  );
}

function TaskDetails({ name, description, isPrivate }: { name: string; description?: string; isPrivate: boolean }) {
  return (
    <DashboardRowDetails
      icon={TASK_ICON}
      title={
        <DashboardPrivateTitle
          title={<MentionedText content={name} />}
          isPrivate={isPrivate}
          privateTooltip="Task is Private"
        />
      }
      text={description}
    />
  );
}

function TaskRowMenuButton({ taskId, onOpen }: { taskId: string; onOpen: () => void }) {
  return (
    <IconButton
      id={actionId(taskId)}
      name="more_vert"
      skipMargin
      title="actions"
      ariaLabel="Task actions"
      data-testid="actions"
      iconClassName="font-extrabold"
      className="!m-0 !p-0 h-10"
      containerClassName="shrink-0"
      onClick={(event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
    />
  );
}

function RecentTaskRow({ task, onOpenMenu }: { task: CRMTask; onOpenMenu: () => void }) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const description = taskPreviewSubtitle(task.name, task.description);
  const openTask = () => {
    trackEvent(TASK_ANALYTICS_EVENTS.opened, {
      ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE),
      openSource: "row"
    });
    navigate(`/tasks/${task.id}` as Path);
  };
  const isPrivate = task.visibility === TaskVisibilityValues.PRIVATE;
  const menuButton = <TaskRowMenuButton taskId={task.id} onOpen={onOpenMenu} />;

  function handleContextMenu(event: MouseEvent<HTMLTableRowElement>) {
    event.preventDefault();
    event.stopPropagation();
    onOpenMenu();
  }

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`recent-task-row-${task.id}`}
      onClick={openTask}
      asButton={false}
      ariaLabel={`Open task ${displayMentionText(task.name)}`}
      onContextMenu={handleContextMenu}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <TaskDetails name={task.name} description={description} isPrivate={isPrivate} />
            <DueLabel dueDate={task.dueDate} />
          </div>
          {menuButton}
        </div>
      }
    >
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "max-w-0")}>
        <TaskDetails name={task.name} description={description} isPrivate={isPrivate} />
      </td>
      <td className="whitespace-nowrap align-middle py-3 text-body-medium text-sys-brand-on-surface">
        <div className="flex items-center justify-end gap-1">
          <DueLabel dueDate={task.dueDate} />
          {menuButton}
        </div>
      </td>
    </TableCardRow>
  );
}

function TeamTaskListLink() {
  return (
    <Link
      to="/tasks/department-tasks"
      className="whitespace-nowrap text-label-medium normal-case tracking-normal text-sys-brand-primary underline underline-offset-2"
      data-testid="recent-tasks-team-link"
    >
      Department Tasks
    </Link>
  );
}

function TasksTableShell({
  totalOpenTaskCount,
  teamName,
  children,
  isStackedTableRows
}: {
  totalOpenTaskCount: number;
  teamName?: string | null;
  children: ReactNode;
  isStackedTableRows: boolean;
}) {
  return (
    <table className={getDashboardPreviewTableClassName(isStackedTableRows)} data-testid="home-recent-tasks-table">
      {isStackedTableRows ? null : (
        <colgroup>
          <col />
          <col className="w-40" />
        </colgroup>
      )}
      <tbody>
        <RowHeader
          label="My Tasks"
          count={totalOpenTaskCount}
          columnCount={COLUMN_COUNT}
          testId="home-my-tasks-count"
          rowTestId="home-my-tasks-header"
          trailing={teamName ? <TeamTaskListLink /> : undefined}
        />
        {children}
      </tbody>
    </table>
  );
}

type RecentTasksTableProps = {
  tasks: CRMTask[];
  loading?: boolean;
  error?: Error;
  totalOpenTaskCount?: number;
  teamName?: string | null;
  canViewTasks?: boolean;
  changeRequests?: Node[];
  changeRequestsLoading?: boolean;
  totalPendingChangeRequestCount?: number;
  changeRequestsSectionId?: string | null;
  showChangeRequestsSection?: boolean;
};

export default function RecentTasksTable({
  tasks,
  loading = false,
  error,
  totalOpenTaskCount = 0,
  teamName,
  canViewTasks = true,
  changeRequests = [],
  changeRequestsLoading = false,
  totalPendingChangeRequestCount = 0,
  changeRequestsSectionId,
  showChangeRequestsSection = false
}: RecentTasksTableProps) {
  const canManageTasks = useCanManageTasksBool();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const isEmpty = !loading && !error && tasks.length === 0;
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { Menu, setMenu } = useTableRowMenu();
  const [taskToDelete, setTaskToDelete] = useState<CRMTask | null>(null);
  const [updateTask, { loading: isUpdatingTask }] = useMutation<UpdateTaskResponse>(UPDATE_TASK_MUTATION, {
    refetchQueries: [TASKS_QUERY],
    awaitRefetchQueries: true
  });

  const handleCompleteTask = useCallback(
    async (task: CRMTask) => {
      if (isUpdatingTask) return;

      try {
        const result = await updateTask({
          variables: { input: { id: task.id, stage: TaskStageValues.COMPLETE } }
        });

        if (result.data?.updateTask?.success) {
          trackEvent(TASK_ANALYTICS_EVENTS.stageChanged, {
            ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE, { includeStage: false }),
            fromStage: task.stage,
            toStage: TaskStageValues.COMPLETE
          });
          showSnackbar("Task completed");
          return;
        }

        showErrorSnackbar(result.data?.updateTask?.message || "Failed to complete task");
      } catch (error) {
        showErrorSnackbar(error instanceof Error ? error.message : "Failed to complete task. Please try again.");
      }
    },
    [isUpdatingTask, trackEvent, updateTask]
  );

  const getTaskActions = useCallback(
    (task: CRMTask): Action[] => {
      const actions: Action[] = [];

      if (task.stage === TaskStageValues.OPEN) {
        actions.push({
          section: "Manage",
          text: "Complete Task",
          onClick: () => void handleCompleteTask(task)
        });
      }

      actions.push({
        section: "Actions",
        text: "View Task",
        onClick: () => {
          trackEvent(TASK_ANALYTICS_EVENTS.opened, {
            ...getTaskAnalyticsData(task, DASHBOARD_TASK_SOURCE),
            openSource: "row_action"
          });
          navigate(`/tasks/${task.id}` as Path);
        }
      });

      if (canManageTasks) {
        actions.push({
          text: "Delete Task",
          iconName: "delete",
          destructive: true,
          onClick: () => setTaskToDelete(task)
        });
      }

      return actions;
    },
    [canManageTasks, handleCompleteTask, navigate, trackEvent]
  );

  return (
    <section data-testid="home-recent-tasks-card" className={DASHBOARD_PANEL_SHELL_CLASS}>
      <Menu />
      <DashboardTasksPanelHeader
        canViewTasks={canViewTasks}
        showChangeRequestsSection={showChangeRequestsSection}
        isEmpty={isEmpty}
      />
      <div
        className={classNames(
          "max-w-full",
          TABLE_CARD_BODY_PADDING,
          DASHBOARD_PANEL_BODY_CLASS,
          DASHBOARD_TABLE_CARD_BODY_CLASS,
          "flex flex-col gap-0"
        )}
      >
        {canViewTasks ? (
          <TasksTableShell
            totalOpenTaskCount={totalOpenTaskCount}
            teamName={teamName}
            isStackedTableRows={isStackedTableRows}
          >
            {loading ? (
              <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
            ) : error ? (
              <tr data-testid="home-recent-tasks-error-row">
                <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-body-medium text-sys-brand-error">
                  Unable to load tasks. Please try again later.
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td colSpan={COLUMN_COUNT}>
                  <EmptyState
                    title="No Open Tasks"
                    caption="It's a beautiful day to Serve the Member."
                    action={{
                      children: "View My Tasks",
                      to: "/tasks/my-tasks"
                    }}
                  />
                </td>
              </tr>
            ) : (
              <>
                {tasks.map((task) => (
                  <RecentTaskRow
                    key={task.id}
                    task={task}
                    onOpenMenu={() => setMenu({ rowId: task.id, actions: getTaskActions(task) })}
                  />
                ))}
                <RowLink columnCount={COLUMN_COUNT} to="/tasks/my-tasks" testId="recent-tasks-view-more">
                  View All
                </RowLink>
              </>
            )}
          </TasksTableShell>
        ) : null}
        {showChangeRequestsSection && changeRequestsSectionId ? (
          <PendingChangeRequestsTable
            changeRequests={changeRequests}
            loading={changeRequestsLoading}
            totalPendingCount={totalPendingChangeRequestCount}
            sectionId={changeRequestsSectionId}
          />
        ) : null}
      </div>
      <DeleteTaskDialog
        open={!!taskToDelete}
        taskId={taskToDelete?.id ?? ""}
        task={taskToDelete ?? undefined}
        source={DASHBOARD_TASK_SOURCE}
        onClose={() => setTaskToDelete(null)}
        onDeleted={() => setTaskToDelete(null)}
      />
    </section>
  );
}
